'use client';

import React, { useState, useEffect } from 'react';
import { X, Mail, FileCheck, ArrowRight, ShieldCheck } from 'lucide-react';

const LEAD_MODAL_DISMISSED_KEY = 'tally_lead_modal_dismissed';
const MIN_SECONDS_BEFORE_EXIT_INTENT = 25; // Don't trigger when user is just signing in or quickly leaving

export default function LeadCaptureModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [exitIntentAllowed, setExitIntentAllowed] = useState(false);

  useEffect(() => {
    const alreadyCaptured = typeof localStorage !== 'undefined' && localStorage.getItem('tally_lead_captured');
    const dismissedPermanently = typeof localStorage !== 'undefined' && localStorage.getItem(LEAD_MODAL_DISMISSED_KEY);
    if (alreadyCaptured || dismissedPermanently) return;

    const t = setTimeout(() => setExitIntentAllowed(true), MIN_SECONDS_BEFORE_EXIT_INTENT * 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!exitIntentAllowed) return;

    const handleMouseOut = (e: MouseEvent) => {
      const alreadyCaptured = localStorage.getItem('tally_lead_captured');
      const dismissed = localStorage.getItem(LEAD_MODAL_DISMISSED_KEY);
      if (e.clientY < 0 && !alreadyCaptured && !dismissed) {
        setIsOpen(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseOut);
    return () => document.removeEventListener('mouseleave', handleMouseOut);
  }, [exitIntentAllowed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: connect to Supabase/Resend/Mailchimp API
    console.log('Lead Captured:', email);

    localStorage.setItem('tally_lead_captured', 'true');
    setSubmitted(true);

    // Auto-close after 3 seconds so they can see the success message
    setTimeout(() => setIsOpen(false), 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative bg-white w-full max-w-[500px] rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Close Button */}
        <button
          type="button"
          onClick={() => {
            if (typeof localStorage !== 'undefined') localStorage.setItem(LEAD_MODAL_DISMISSED_KEY, 'true');
            setIsOpen(false);
          }}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X size={20} />
        </button>

        {!submitted ? (
          <div className="p-8">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
              <FileCheck className="text-amber-600" size={28} />
            </div>

            <h3 className="text-[28px] md:text-[30px] font-bold text-[#002b1b] mb-3 leading-tight">
              Wait! Are carriers shorting your commissions?
            </h3>

            <p className="text-slate-600 text-[15px] mb-8 leading-relaxed">
              Before you go, grab our <strong>2026 Commission Leakage Checklist</strong>. We identify the 5 most common
              &quot;clerical errors&quot; that cost Medicare agents thousands in renewals every year.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="email"
                  required
                  placeholder="Enter your business email"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#66d396] focus:border-transparent transition-all"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#001f14] text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-black transition-all shadow-lg group"
              >
                Send me the Checklist
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>

            <div className="mt-6 flex items-center justify-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
              <ShieldCheck size={14} />
              No Spam. Just Revenue Recovery.
            </div>
          </div>
        ) : (
          <div className="p-12 text-center animate-in fade-in zoom-in-95 duration-500">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileCheck className="text-green-600" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-[#00301f] mb-2">Check your inbox!</h3>
            <p className="text-slate-500 text-[15px]">
              The checklist is on its way. <br />
              Keep an eye out for our <strong>Founder Rate</strong> invitation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

